import { AdjustCropRect, BLUR_UNITS_PER_STRENGTH, unitOf } from './adjust.types'
import { CropHandle, Point, ViewTransform, apply as applyMatrix, dragCropRect, frameRatio, hitCropHandle, invert, totalRotation, zoomAbout } from './adjustGeometry'
import { KeyboardEvent, PointerEvent, RefObject, useCallback, useRef } from 'react'
import { frameScreenRect, stickerHandleOnScreen } from './adjustView'
import { hitSticker } from './adjustStickers'

import { GESTURE_KEY_PREFIX } from './adjustDocument'
import { useAdjustEditor } from './AdjustEditor.context'

type Gesture =
  | { kind: 'split' }
  | { kind: 'pan', start: Point, pan: Point }
  | { kind: 'crop', handle: CropHandle, start: Point, rect: AdjustCropRect }
  | { kind: 'blur', id: string, start: Point, isAdded: boolean }
  | { kind: 'shape', id: string, start: Point, isAdded: boolean }
  | { kind: 'move', id: string, offset: Point }
  | { kind: 'scale', id: string, startDistance: number, width: number, height: number }

const CROP_HANDLE_TOLERANCE_PX = 14
const STICKER_HANDLE_RADIUS_PX = 16
/** A drag shorter than this (screen px) does not create a line, rectangle or area blur */
const MIN_DRAG_PX = 4

const CURSORS: Record<CropHandle, string> = { move: 'move', n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize' }

/**
 * Pointer, wheel and keyboard handling of the Adjust canvas. Every drag is one undo step (a `gesture:` history key,
 * closed by `commit` on release).
 */
export const useAdjustPointer = (canvasRef: RefObject<HTMLCanvasElement>, viewRef: RefObject<ViewTransform | null>) => {
  const editor = useAdjustEditor()
  const editorRef = useRef(editor)
  editorRef.current = editor
  const gestureRef = useRef<Gesture | null>(null)

  const locate = useCallback((event: { clientX: number, clientY: number }) => {
    const canvas = canvasRef.current
    const view = viewRef.current
    if (!canvas || !view) return null
    const rect = canvas.getBoundingClientRect()
    const screen = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    return { screen, image: applyMatrix(invert(view.imageToScreen), screen), frame: applyMatrix(invert(view.frameToScreen), screen), view }
  }, [canvasRef, viewRef])

  const onPointerDown = useCallback((event: PointerEvent<HTMLCanvasElement>) => {
    const { base, doc, apply, tool, viewMode, zoom, pan, blurMode, blurStrength, blurBrushSize, drawTool, drawColor, drawSize, drawOpacity, selectedStickerId, setSelectedStickerId } = editorRef.current
    const at = locate(event)
    if (!base || !at || event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    event.currentTarget.focus()
    const unit = unitOf(base.size.width, base.size.height)
    const panGesture: Gesture | null = zoom > 1 ? { kind: 'pan', start: at.screen, pan } : null
    const id = crypto.randomUUID()

    if (viewMode === 'compare') {
      gestureRef.current = { kind: 'split' }
    } else if (viewMode === 'before') {
      gestureRef.current = panGesture
    } else if (tool === 'crop') {
      const handle = hitCropHandle(at.frame, at.view.frame, CROP_HANDLE_TOLERANCE_PX / at.view.scale)
      gestureRef.current = handle ? { kind: 'crop', handle, start: at.frame, rect: at.view.frame } : panGesture
    } else if (tool === 'redact') {
      if (blurMode === 'brush') {
        apply({ type: 'addBlur', blur: { id, mode: 'brush', radius: blurStrength * BLUR_UNITS_PER_STRENGTH * unit, size: blurBrushSize * unit, points: [at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + id)
      }
      gestureRef.current = { kind: 'blur', id, start: at.screen, isAdded: blurMode === 'brush' }
    } else if (tool === 'annotate') {
      const isFreehand = drawTool === 'pen' || drawTool === 'eraser'
      if (isFreehand) {
        apply({ type: 'addShape', shape: { id, tool: drawTool, color: drawColor, size: drawSize * unit, opacity: drawOpacity / 100, points: [at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + id)
      }
      gestureRef.current = { kind: 'shape', id, start: at.screen, isAdded: isFreehand }
    } else if (tool === 'sticker') {
      const selected = doc.stickers.find(sticker => sticker.id === selectedStickerId)
      const handle = selected ? stickerHandleOnScreen(at.view, selected) : null
      if (selected && handle && Math.hypot(handle.x - at.screen.x, handle.y - at.screen.y) <= STICKER_HANDLE_RADIUS_PX) {
        gestureRef.current = { kind: 'scale', id: selected.id, startDistance: Math.max(1, Math.hypot(at.image.x - selected.x, at.image.y - selected.y)), width: selected.width, height: selected.height }
      } else {
        const hit = hitSticker(doc.stickers, at.image)
        setSelectedStickerId(hit?.id ?? null)
        gestureRef.current = hit ? { kind: 'move', id: hit.id, offset: { x: at.image.x - hit.x, y: at.image.y - hit.y } } : panGesture
      }
    } else {
      gestureRef.current = panGesture
    }
  }, [locate])

  const updateCursor = useCallback((screen: Point, frame: Point, image: Point, view: ViewTransform) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const { tool, viewMode, zoom, doc, selectedStickerId } = editorRef.current
    let cursor = zoom > 1 ? 'grab' : 'default'
    if (viewMode === 'compare') cursor = 'ew-resize'
    else if (viewMode === 'after' && tool === 'crop') {
      const handle = hitCropHandle(frame, view.frame, CROP_HANDLE_TOLERANCE_PX / view.scale)
      if (handle) cursor = CURSORS[handle]
    } else if (viewMode === 'after' && tool === 'redact') {
      // Brush: the canvas draws a circle the size of the brush instead (AdjustCanvas); Area: the default cross
      cursor = editorRef.current.blurMode === 'brush' ? 'none' : 'crosshair'
    } else if (viewMode === 'after' && tool === 'annotate') cursor = 'crosshair'
    else if (viewMode === 'after' && tool === 'sticker') {
      const selected = doc.stickers.find(sticker => sticker.id === selectedStickerId)
      const handle = selected ? stickerHandleOnScreen(view, selected) : null
      if (handle && Math.hypot(handle.x - screen.x, handle.y - screen.y) <= STICKER_HANDLE_RADIUS_PX) cursor = 'nwse-resize'
      else if (hitSticker(doc.stickers, image)) cursor = 'move'
    }
    canvas.style.cursor = cursor
  }, [canvasRef])

  const onPointerMove = useCallback((event: PointerEvent<HTMLCanvasElement>) => {
    const at = locate(event)
    if (!at) return
    const gesture = gestureRef.current
    if (!gesture) {
      updateCursor(at.screen, at.frame, at.image, at.view)
      return
    }
    const { base, doc, apply, setSplit, setPan, blurMode, blurStrength, drawTool, drawColor, drawSize, drawOpacity } = editorRef.current
    if (!base) return
    const unit = unitOf(base.size.width, base.size.height)
    const isFar = (start: Point) => Math.hypot(at.screen.x - start.x, at.screen.y - start.y) >= MIN_DRAG_PX

    switch (gesture.kind) {
      case 'split': {
        const rect = frameScreenRect(at.view)
        setSplit(Math.min(1, Math.max(0, (at.screen.x - rect.x) / Math.max(1, rect.width))))
        break
      }
      case 'pan':
        setPan({ x: gesture.pan.x + at.screen.x - gesture.start.x, y: gesture.pan.y + at.screen.y - gesture.start.y })
        break
      case 'crop': {
        const ratio = doc.crop.aspect === 'custom' ? null : frameRatio(doc.crop, base.size)
        const minSize = Math.min(base.size.width, base.size.height) * 0.05
        const rect = dragCropRect(gesture.rect, gesture.handle, { x: at.frame.x - gesture.start.x, y: at.frame.y - gesture.start.y }, ratio, totalRotation(doc.crop), base.size, minSize)
        apply({ type: 'setCrop', crop: { rect } }, `${GESTURE_KEY_PREFIX}crop`)
        break
      }
      case 'blur': {
        const blur = doc.blurs.find(item => item.id === gesture.id)
        if (!gesture.isAdded) {
          if (!isFar(gesture.start)) break
          const origin = applyMatrix(invert(at.view.imageToScreen), gesture.start)
          apply({ type: 'addBlur', blur: { id: gesture.id, mode: blurMode, radius: blurStrength * BLUR_UNITS_PER_STRENGTH * unit, size: 0, points: [origin.x, origin.y, at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + gesture.id)
          gestureRef.current = { ...gesture, isAdded: true }
        } else if (blur?.mode === 'area') {
          apply({ type: 'updateBlur', id: gesture.id, patch: { points: [blur.points[0], blur.points[1], at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + gesture.id)
        } else if (blur) {
          const [lastX, lastY] = blur.points.slice(-2)
          if (Math.hypot(at.image.x - lastX, at.image.y - lastY) >= unit * 2) {
            apply({ type: 'updateBlur', id: gesture.id, patch: { points: [...blur.points, at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + gesture.id)
          }
        }
        break
      }
      case 'shape': {
        const shape = doc.shapes.find(item => item.id === gesture.id)
        if (!gesture.isAdded) {
          if (!isFar(gesture.start)) break
          const origin = applyMatrix(invert(at.view.imageToScreen), gesture.start)
          apply({ type: 'addShape', shape: { id: gesture.id, tool: drawTool, color: drawColor, size: drawSize * unit, opacity: drawOpacity / 100, points: [origin.x, origin.y, at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + gesture.id)
          gestureRef.current = { ...gesture, isAdded: true }
        } else if (shape && (shape.tool === 'pen' || shape.tool === 'eraser')) {
          const [lastX, lastY] = shape.points.slice(-2)
          if (Math.hypot(at.image.x - lastX, at.image.y - lastY) >= unit) {
            apply({ type: 'updateShape', id: gesture.id, patch: { points: [...shape.points, at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + gesture.id)
          }
        } else if (shape) {
          apply({ type: 'updateShape', id: gesture.id, patch: { points: [shape.points[0], shape.points[1], at.image.x, at.image.y] } }, GESTURE_KEY_PREFIX + gesture.id)
        }
        break
      }
      case 'move':
        apply({ type: 'updateSticker', id: gesture.id, patch: { x: at.image.x - gesture.offset.x, y: at.image.y - gesture.offset.y } }, GESTURE_KEY_PREFIX + gesture.id)
        break
      case 'scale': {
        const sticker = doc.stickers.find(item => item.id === gesture.id)
        if (!sticker) break
        const factor = Math.max(0.1, Math.hypot(at.image.x - sticker.x, at.image.y - sticker.y) / gesture.startDistance)
        apply({ type: 'updateSticker', id: gesture.id, patch: { width: gesture.width * factor, height: gesture.height * factor } }, GESTURE_KEY_PREFIX + gesture.id)
        break
      }
    }
  }, [locate, updateCursor])

  const onPointerUp = useCallback((event: PointerEvent<HTMLCanvasElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    if (!gestureRef.current) return
    gestureRef.current = null
    editorRef.current.commit()
  }, [])

  /** Native (non-passive) wheel listener: Ctrl/⌘ + wheel or a trackpad pinch zooms about the cursor, else pans */
  const onWheel = useCallback((event: globalThis.WheelEvent) => {
    const { zoom, setZoom, pan, setPan } = editorRef.current
    const at = locate(event)
    const canvas = canvasRef.current
    if (!at || !canvas) return
    if (event.ctrlKey || event.metaKey || zoom > 1) event.preventDefault()
    if (event.ctrlKey || event.metaKey) {
      const next = Math.min(8, Math.max(1, zoom * Math.exp(-event.deltaY * 0.01)))
      const viewport = { width: canvas.clientWidth, height: canvas.clientHeight }
      setPan(next === 1 ? { x: 0, y: 0 } : zoomAbout(at.screen, viewport, pan, zoom, next))
      setZoom(next)
    } else if (zoom > 1) {
      setPan({ x: pan.x - event.deltaX, y: pan.y - event.deltaY })
    }
  }, [canvasRef, locate])

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLCanvasElement>) => {
    const { tool, selectedStickerId, setSelectedStickerId, apply } = editorRef.current
    if (tool !== 'sticker' || !selectedStickerId) return
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault()
      apply({ type: 'removeSticker', id: selectedStickerId })
      setSelectedStickerId(null)
    } else if (event.key === 'Escape') {
      setSelectedStickerId(null)
    }
  }, [])

  return { onPointerDown, onPointerMove, onPointerUp, onWheel, onKeyDown }
}
